import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { Pause, Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    FILM_DURATION,
    SCENES,
    formatClock,
    progress
} from '@/components/landing/film/timeline'

import {
    fixed,
    setStyle,
    setText
} from '@/components/landing/film/overlay-utils'

export type FilmFormat = 'cinema' | 'vertical'

interface FilmControlsProps {
    register: RegisterUpdater
    playing: boolean
    ended: boolean
    sceneIndex: number
    format: FilmFormat
    onToggle: () => void
    onReplay: () => void
    onSeek: (time: number) => void
    onFormat: (format: FilmFormat) => void
}

const FilmControls: FC<FilmControlsProps> = ({
    register,
    playing,
    ended,
    sceneIndex,
    format,
    onToggle,
    onReplay,
    onSeek,
    onFormat
}): ReactNode => {
    const fillRefs = useRef<Array<HTMLSpanElement | null>>([])
    const timeRef = useRef<HTMLSpanElement>(null)

    useEffect(() => register((t) => {
        SCENES.forEach((scene, i) => {
            setStyle(fillRefs.current[i], 'transform', `scaleX(${fixed(progress(t, scene.start, scene.end), 4)})`)
        })
        setText(timeRef.current, formatClock(t))
    }), [register])

    const toggleLabel = ended ? 'Replay film' : playing ? 'Pause film' : 'Play film'

    return (
        <div className='absolute inset-x-[3%] bottom-[4.5%] z-20 flex items-center gap-3 opacity-80 transition-opacity duration-300 hover:opacity-100 focus-within:opacity-100 sm:gap-4'>
            <button
                type='button'
                onClick={ended ? onReplay : onToggle}
                aria-label={toggleLabel}
                className='flex size-9 shrink-0 items-center justify-center rounded-full border border-titanium/40 bg-obsidian/60 text-white backdrop-blur-sm transition-colors hover:border-gold hover:text-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
            >
                {ended ? <RotateCcw className='size-4' /> : playing ? <Pause className='size-4' /> : <Play className='size-4 translate-x-px' />}
            </button>

            <div className='flex min-w-0 flex-1 items-center gap-1' role='group' aria-label='Film chapters'>
                {SCENES.map((scene, i) => (
                    <button
                        key={scene.id}
                        type='button'
                        onClick={() => onSeek(scene.start)}
                        aria-label={`Watch scene ${scene.number}, ${scene.title}`}
                        aria-current={sceneIndex === i ? 'step' : undefined}
                        className='group relative flex h-8 min-w-0 items-center focus-visible:outline-2 focus-visible:outline-gold'
                        style={{ flexGrow: scene.end - scene.start, flexBasis: 0 }}
                    >
                        <span className={cn('pointer-events-none absolute bottom-full left-0 hidden truncate pb-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors md:block', sceneIndex === i ? 'text-gold' : 'text-titanium group-hover:text-titanium-light')}>
                            {scene.number} {scene.title}
                        </span>
                        <span className='relative h-[3px] w-full overflow-hidden rounded-full bg-white/15 transition-[height] group-hover:h-[5px]'>
                            <span
                                ref={(element) => {
                                    fillRefs.current[i] = element
                                }}
                                className='absolute inset-0 origin-left bg-gold'
                                style={{ transform: 'scaleX(0)' }}
                            />
                        </span>
                    </button>
                ))}
            </div>

            <span className='hidden shrink-0 font-mono text-[11px] tabular-nums tracking-[0.18em] text-titanium-light sm:inline'>
                <span ref={timeRef}>00:00</span>
                <span className='text-titanium'> / {formatClock(FILM_DURATION)}</span>
            </span>

            <div className='flex shrink-0 overflow-hidden rounded-full border border-titanium/40 bg-obsidian/60 backdrop-blur-sm' role='group' aria-label='Film format'>
                {(['cinema', 'vertical'] as FilmFormat[]).map((option) => (
                    <button
                        key={option}
                        type='button'
                        onClick={() => onFormat(option)}
                        aria-pressed={format === option}
                        aria-label={option === 'cinema' ? 'Cinema format, 16 by 9' : 'Vertical format, 9 by 16'}
                        className={cn(
                            'px-2.5 py-1.5 font-mono text-[10px] tracking-[0.14em] transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gold sm:px-3',
                            format === option ? 'bg-gold text-obsidian' : 'text-titanium-light hover:text-white'
                        )}
                    >
                        {option === 'cinema' ? '16:9' : '9:16'}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default FilmControls
